import React, { useEffect, useState } from "react";
import { Dialog, Button, Spinner, Badge } from "@/components/ui";
import { SmartLink, SmartLinkStats } from "@/services/bedrock/dtos";
import { SmartLinkServices } from "@/services/bedrock";
import { BarChart3, FileDown } from "lucide-react";
import { kFormatter } from "@/utils";
import { exportToXlsx, getAutoWidths } from "@/utils/xlsxExportHelpers";
import { format, startOfMonth } from "date-fns";
import { ReportDateRange } from "@/components/ReportDateRange";
import { DateRange } from "react-day-picker";
import { XlsxSheet } from "@/interfaces";
import { toast } from "sonner";
import { ReportTabs } from "@/containers/Coach/SmartLink/StatsDialog/components/ReportTabs";
import { generateTabs } from "@/containers/Coach/SmartLink/StatsDialog/statsExportHelper";

interface SmartLinkStatsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  smartLink: SmartLink;
}

export const SmartLinkStatsDialog: React.FC<SmartLinkStatsDialogProps> = ({ open, onOpenChange, smartLink }) => {
  const [stats, setStats] = useState<SmartLinkStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [date, setDate] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: new Date(),
  });
  const [preset, setPreset] = useState<string>("this_month");

  const handleExport = async () => {
    if (!stats) return;

    setIsExporting(true);
    try {
      const dateRangeString =
        preset === "all_time"
          ? "Timeframe: All Time"
          : `Timeframe: ${format(date!.from!, "LLL dd, y")} - ${format(date?.to || new Date(), "LLL dd, y")}`;

      const preparedByString = `Prepared by SocialCoach on ${format(new Date(), "PPp")}`;
      const linkInfoString = `Smart Link: ${smartLink.name} (${smartLink.destinationUrl})`;
      const totalClicksString = `Total Clicks: ${stats.totalClicks}`;
      const reportConfig = generateTabs(stats);

      const sheets: XlsxSheet[] = reportConfig.map(config => ({
        name: config.name.substring(0, 31),
        data: config.data,
        columns: getAutoWidths(config.data),
        headerRows: [
          [preparedByString],
          [linkInfoString],
          [dateRangeString],
          [totalClicksString],
          [`Report: ${config.name}`],
          [`Description: ${config.desc}`],
        ],
      }));

      const filename = `SmartLink_Stats_${smartLink.name.replace(/\s+/g, "_")}_${format(new Date(), "yyyy-MM-dd")}`;
      await exportToXlsx(filename, sheets);
      toast.success("Excel report exported successfully!");
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to generate Excel report.");
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (open && smartLink) {
      if (preset !== "all_time" && !date?.from) return;

      setIsLoading(true);
      SmartLinkServices.getSmartLinkStats(
        smartLink.id,
        [
          "total_clicks",
          "clicks_by_day",
          "clicks_by_network",
          "clicks_by_region",
          "clicks_by_player",
          "clicks_by_prompt",
          "clicks_by_campaign",
        ],
        date?.from ? format(date.from, "yyyy-MM-dd") : undefined,
        date?.to ? format(date.to, "yyyy-MM-dd") : undefined
      )
        .then(setStats)
        .catch(console.error)
        .finally(() => setIsLoading(false));
    }
  }, [open, smartLink, date, preset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="2xl">
      <Dialog.Header>
        <div className="flex items-center justify-between w-full gap-6">
          <div className="flex flex-col gap-1">
            <Dialog.Title className="text-xl">Smart Link Analytics</Dialog.Title>
            <Dialog.Description className="flex flex-col items-start gap-0.5 mt-1">
              <span className="font-semibold text-foreground text-base leading-tight">{smartLink.name}</span>
              <span className="text-xs font-medium text-slate-500">{smartLink.destinationUrl}</span>
            </Dialog.Description>
          </div>
          {stats && (
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-end">
                <Badge
                  variant="secondary"
                  className="text-lg font-bold px-3 py-1 bg-primary/5 text-primary border-primary/20 text-nowrap"
                >
                  {stats.totalClicks ? kFormatter(stats.totalClicks) : 0} Clicks
                </Badge>
              </div>
            </div>
          )}
        </div>
      </Dialog.Header>

      <Dialog.Content className="min-h-[500px] overflow-y-auto max-h-[calc(100vh-20rem)]">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <Spinner className="size-8" />
            <p className="text-muted-foreground animate-pulse">Generating report...</p>
          </div>
        ) : stats ? (
          <div className={"flex flex-col gap-6"}>
            <ReportDateRange date={date} preset={preset} onDateChange={setDate} onPresetChange={setPreset} />
            {stats.totalClicks > 0 ? (
              <ReportTabs stats={stats} />
            ) : (
              <div className="flex flex-col items-center justify-center py-20 px-6 text-center gap-6 bg-slate-50 border border-dashed border-slate-200 rounded-3xl min-h-[400px]">
                <div className="bg-white p-5 rounded-full shadow-sm border border-slate-100/50">
                  <BarChart3 className="size-12 text-slate-300" />
                </div>
                <div className="flex flex-col gap-2 max-w-[340px]">
                  <h3 className="font-bold text-xl text-slate-900 tracking-tight">No clicks to report yet</h3>
                  <p className="text-sm text-slate-500 leading-relaxed font-sans">
                    We haven't recorded any clicks for this smart link during the selected timeframe. Try expanding your
                    date range or sharing the link in a new campaign.
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-32 px-6 text-center gap-6 text-muted-foreground">
            <div className="bg-slate-50 p-5 rounded-full border border-slate-100">
              <BarChart3 className="size-12 opacity-20" />
            </div>
            <div className="flex flex-col gap-1 max-w-[300px]">
              <h3 className="font-bold text-lg text-slate-900">Analytics unavailable</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                We couldn't retrieve statistical data for this link. Please try again later.
              </p>
            </div>
          </div>
        )}
      </Dialog.Content>

      <Dialog.Footer>
        <Button variant="outline" onClick={handleExport} disabled={isExporting} loading={isExporting}>
          <FileDown className="size-4" />
          Export
        </Button>
        <Button onClick={() => onOpenChange(false)}>Close Report</Button>
      </Dialog.Footer>
    </Dialog>
  );
};
